import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Grid,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';
import TenantPicker from '../TenantPicker';

const CommercialAllocationManagement = ({ propertyId, property, onClose }) => {
  const { formatMoney } = useCurrency();
  const [allocations, setAllocations] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleAllocations, resultCount, totalCount
  } = useTableSearch(allocations, {
    searchFields: ['tenant.name', 'notes'],
    filters: {
      status: (row, v) => row.status === v
    }
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    tenant: '',
    squareFeet: 0,
    monthlyRent: 0,
    deposit: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'active',
    description: '',
  });

  useEffect(() => {
    if (propertyId) {
      fetchAllocations();
    }
    // Keyed on the property being shown. fetchAllocations is declared below and reads only
    // propertyId, so naming it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const fetchAllocations = async () => {
    try {
      const response = await axios.get(`/commercial-allocations?property=${propertyId}`);
      setAllocations(response.data);
    } catch (error) {
      toast.error('Error fetching allocations');
    }
  };

  const handleOpen = (allocation = null) => {
    if (allocation) {
      setEditing(allocation._id);
      setFormData({
        tenant: allocation.tenant?._id || allocation.tenant || '',
        squareFeet: allocation.squareFeet || 0,
        monthlyRent: allocation.monthlyRent || 0,
        deposit: allocation.deposit || 0,
        startDate: allocation.startDate 
          ? new Date(allocation.startDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        endDate: allocation.endDate 
          ? new Date(allocation.endDate).toISOString().split('T')[0]
          : '',
        status: allocation.status || 'active',
        description: allocation.description || '',
      });
    } else {
      setEditing(null);
      setFormData({
        tenant: '',
        squareFeet: 0,
        monthlyRent: 0,
        deposit: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        status: 'active',
        description: '',
      });
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.tenant) {
        toast.error('Tenant is required');
        return;
      }
      if (!formData.squareFeet || formData.squareFeet <= 0) {
        toast.error('Square feet must be greater than 0');
        return;
      }

      if (editing) {
        await axios.put(`/commercial-allocations/${editing}`, formData);
        toast.success('Allocation updated successfully');
      } else {
        await axios.post('/commercial-allocations', {
          ...formData,
          property: propertyId
        });
        toast.success('Allocation created successfully');
      }
      setOpen(false);
      fetchAllocations();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving allocation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this allocation? This action cannot be undone.')) return;
    try {
      await axios.delete(`/commercial-allocations/${id}`);
      toast.success('Allocation deleted successfully');
      fetchAllocations();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting allocation');
    }
  };

  const allocatedSquareFeet = allocations
    .filter(a => a.status === 'active')
    .reduce((sum, a) => sum + (a.squareFeet || 0), 0);
  const availableSquareFeet = (property?.squareFeet || property?.totalSquareFeet || 0) - allocatedSquareFeet;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Square Feet Allocations</Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {(property?.squareFeet || property?.totalSquareFeet || 0).toLocaleString()} sq ft | 
            Allocated: {allocatedSquareFeet.toLocaleString()} sq ft | 
            Available: {availableSquareFeet.toLocaleString()} sq ft
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          disabled={availableSquareFeet <= 0}
        >
          Add Allocation
        </Button>
      </Box>

      {availableSquareFeet <= 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2">
            All square feet have been allocated. No more allocations can be created.
          </Typography>
        </Box>
      )}

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Tenant name"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'status', label: 'Status', value: filterValues.status || '',
            onChange: (v) => setFilter('status', v),
            options: [{ value: 'active', label: 'Active' }, { value: 'ended', label: 'Ended' }] }
        ]}
      />
      <ScrollTable size="small">
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Tenant</TableCell>
              <TableCell>Square Feet</TableCell>
              <TableCell>Monthly Rent</TableCell>
              <TableCell>Deposit</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleAllocations.map((allocation) => (
              <TableRow key={allocation._id}>
                <TableCell className="pin-left">
                  {allocation.tenant?.user?.name || '-'}
                </TableCell>
                <TableCell>{allocation.squareFeet?.toLocaleString()}</TableCell>
                <TableCell>{formatMoney(allocation.monthlyRent, { decimals: 0 })}</TableCell>
                <TableCell>{formatMoney(allocation.deposit, { decimals: 0 })}</TableCell>
                <TableCell>
                  {allocation.startDate 
                    ? new Date(allocation.startDate).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {allocation.endDate 
                    ? new Date(allocation.endDate).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={allocation.status}
                    color={allocation.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell className="pin-right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpen(allocation)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(allocation._id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {visibleAllocations.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No allocations found. Add allocations to this commercial property.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? 'Edit Allocation' : 'Add New Allocation'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TenantPicker
                valueId={formData.tenant}
                required
                onChange={(val) => setFormData({ ...formData, tenant: val?._id || '' })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Square Feet"
                type="number"
                required
                value={formData.squareFeet}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, squareFeet: parseInt(e.target.value) || 0 })
                }
                helperText={`Available: ${availableSquareFeet.toLocaleString()} sq ft`}
                inputProps={{ max: availableSquareFeet + (editing ? allocations.find(a => a._id === editing)?.squareFeet || 0 : 0) }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                required
                value={formData.monthlyRent}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, monthlyRent: parseFloat(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Deposit"
                type="number"
                value={formData.deposit}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, deposit: parseFloat(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="terminated">Terminated</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date (Optional)"
                type="date"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommercialAllocationManagement;

